import React, {useEffect, useRef, useState} from "react";
import styles from "./EmailSendingLogTransition.module.css";
import {EmailSendingLogUi, EmailSendingLogUiEmailTypeEnum, TranscriptRoutingDecisionUi} from "../../../../../../../api/generated";
import {Arrow} from "../../../../../../elements/arrow/Arrow";
import {formatDateTime} from "../../../../../../../utils/dateTimeUtil";
import {getEmailSendingTypeTitle} from "../../../../../../../utils/enumValueTitleUtil";
import {RoutingDetails} from "../../../routingDetails/RoutingDetails";

interface EmailSendingLogTransitionProps {
    log: EmailSendingLogUi;
    routingDecision?: TranscriptRoutingDecisionUi;
}

const emptyValue = "—";

const getEmailTypeLabel = (emailType?: EmailSendingLogUiEmailTypeEnum): string => {
    return emailType ? getEmailSendingTypeTitle(emailType) : emptyValue;
};

const formatRecipients = (recipients?: string[]): string => {
    const filteredRecipients = (recipients || [])
        .map(recipient => recipient.trim())
        .filter(Boolean);
    return filteredRecipients.length ? filteredRecipients.join(", ") : emptyValue;
};

const formatCcRecipients = (ccRecipients?: { [key: string]: string }): string => {
    const formattedRecipients = Object.entries(ccRecipients || {})
        .map(([title, email]) => {
            const normalizedTitle = title.trim();
            const normalizedEmail = email.trim();
            if (!normalizedEmail) {
                return "";
            }
            return normalizedTitle ? `${normalizedTitle}: ${normalizedEmail}` : normalizedEmail;
        })
        .filter(Boolean);

    return formattedRecipients.length ? formattedRecipients.join(", ") : emptyValue;
};

const formatSentAt = (sentAt?: string): string => {
    if (!sentAt) {
        return emptyValue;
    }

    const {date, time, timezone} = formatDateTime(sentAt);
    return `${date} ${time} ${timezone}`;
};

export const EmailSendingLogTransition: React.FC<EmailSendingLogTransitionProps> = ({log, routingDecision}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isFullyOpen, setIsFullyOpen] = useState(false);
    const [contentHeight, setContentHeight] = useState(0);
    const detailsContentRef = useRef<HTMLDivElement>(null);
    const emailType = getEmailTypeLabel(log.emailType);
    const sentAt = formatSentAt(log.sentAt);

    // Uncapped once open, so the nested routing details animate at their own pace.
    const toggleOpen = () => {
        if (!isOpen) {
            setIsOpen(true);
            return;
        }
        setIsFullyOpen(false);
        requestAnimationFrame(() => setIsOpen(false));
    };

    // Keep the collapsed height in sync with the content, which grows when the nested routing details expand.
    useEffect(() => {
        const detailsContent = detailsContentRef.current;
        if (!detailsContent) {
            return;
        }

        const observer = new ResizeObserver(() => setContentHeight(detailsContent.scrollHeight));
        observer.observe(detailsContent);
        return () => observer.disconnect();
    }, []);

    return <div className={styles.wrapper}>
        <div className={styles.preview}>
            <div className={styles.line}/>
            <div
                className={styles.content}
                onClick={toggleOpen}
            >
                <img className={styles.icon} src={"/toolCallIcon.svg"} alt="Email sent"/>
                <span className={styles.text}>{emailType} email sent</span>
                <Arrow isUp={isOpen} onClick={toggleOpen}/>
            </div>
            <div className={styles.line}/>
        </div>
        <div
            className={`${styles.details} ${isOpen ? styles.detailsOpen : ""}`}
            style={{maxHeight: !isOpen ? 0 : isFullyOpen ? "none" : `${contentHeight}px`}}
            onTransitionEnd={event => {
                if (event.target === event.currentTarget && event.propertyName === "max-height" && isOpen) {
                    setIsFullyOpen(true);
                }
            }}
        >
            <div className={styles.detailsContent} ref={detailsContentRef}>
                <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Email type</span>
                    <span className={styles.detailValue}>{emailType}</span>
                </div>
                <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Sent at</span>
                    <span className={styles.detailValue}>{sentAt}</span>
                </div>
                <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Recipients</span>
                    <span className={styles.detailValue}>{formatRecipients(log.recipients)}</span>
                </div>
                <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>CC recipients</span>
                    <span className={styles.detailValue}>{formatCcRecipients(log.ccRecipients)}</span>
                </div>
                {routingDecision && (
                    <RoutingDetails routingDecision={routingDecision} collapsible={true}/>
                )}
            </div>
        </div>
    </div>
}
